import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { 
  Calendar, 
  Phone, 
  ArrowRight, 
  Plus, 
  Minus, 
  ArrowUp,
  Check
} from 'lucide-react';

// Custom SVG Icons from the scraped HTML to maintain 100% visual fidelity
const SVGIcon = ({ type, className = "w-8 h-8" }) => {
  const baseSvgProps = {
    className: `${className} stroke-current fill-none`,
    viewBox: "0 0 48 48",
    strokeWidth: "1.7",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true"
  };

  switch (type) {
    case 'eye':
      return (
        <svg {...baseSvgProps}>
          <path d="M7 24s7-11 17-11 17 11 17 11-7 11-17 11S7 24 7 24z" />
          <circle cx="24" cy="24" r="5" />
        </svg>
      );
    case 'chat':
      return (
        <svg {...baseSvgProps}>
          <path d="M8 10h32v24H23l-10 8v-8H8z" />
          <path d="M17 20h14M17 27h9" />
        </svg>
      );
    case 'loop':
      return (
        <svg {...baseSvgProps}>
          <path d="M11 17a15 15 0 0 1 25-5l4 4" />
          <path d="M40 9v7h-7M37 31a15 15 0 0 1-25 5l-4-4" />
          <path d="M8 39v-7h7" />
        </svg>
      );
    case 'checklist':
      return (
        <svg {...baseSvgProps}>
          <rect height="34" rx="3" width="30" x="9" y="7" />
          <path d="m15 17 3 3 5-6M15 28l3 3 5-6M27 17h7M27 28h7" />
        </svg>
      );
    case 'plus-medical':
      return (
        <svg {...baseSvgProps}>
          <path d="M20 7h8v10h10v8H28v10h-8V25H10v-8h10z" />
        </svg>
      );
    case 'search':
      return (
        <svg {...baseSvgProps}>
          <circle cx="21" cy="21" r="13" />
          <path d="m31 31 10 10" />
        </svg>
      );
    case 'shield-check':
      return (
        <svg {...baseSvgProps}>
          <path d="M24 5 39 11v10c0 10-6 18-15 22C15 39 9 31 9 21V11z" />
          <path d="M17 24h14" />
        </svg>
      );
    case 'question-circle':
      return (
        <svg {...baseSvgProps}>
          <circle cx="24" cy="24" r="17" />
          <path d="M18 18c1-5 11-6 13 0 2 6-7 6-7 12" />
          <circle cx="24" cy="36" fill="currentColor" r="1.5" stroke="none" />
        </svg>
      );
    case 'clock':
      return (
        <svg {...baseSvgProps}>
          <circle cx="24" cy="24" r="17" />
          <path d="M24 13v12l8 5" />
        </svg>
      );
    case 'branch':
      return (
        <svg {...baseSvgProps}>
          <path d="M24 7v34M24 18 12 7M24 30l12 11" />
          <circle cx="12" cy="7" r="3" />
          <circle cx="36" cy="41" r="3" />
        </svg>
      );
    case 'arrow-right-circle':
      return (
        <svg {...baseSvgProps}>
          <path d="M7 24h34" />
          <path d="m28 14 10 10-10 10" />
          <circle cx="15" cy="24" r="6" />
        </svg>
      );
    case 'search-plus':
      return (
        <svg {...baseSvgProps}>
          <circle cx="20" cy="20" r="12" />
          <path d="m29 29 11 11" />
          <path d="M14 20h12M20 14v12" />
        </svg>
      );
    case 'sliders':
      return (
        <svg {...baseSvgProps}>
          <path d="M8 12h32M8 24h32M8 36h32" />
          <circle cx="18" cy="12" r="4" />
          <circle cx="31" cy="24" r="4" />
          <circle cx="22" cy="36" r="4" />
        </svg>
      );
    case 'user-focus':
      return (
        <svg {...baseSvgProps}>
          <circle cx="24" cy="24" r="16" />
          <circle cx="24" cy="24" r="7" />
          <path d="M24 3v7M24 38v7M3 24h7M38 24h7" />
        </svg>
      );
    case 'target':
      return (
        <svg {...baseSvgProps}>
          <circle cx="24" cy="24" r="16" />
          <circle cx="24" cy="24" r="7" />
          <path d="M24 3v7M24 38v7M3 24h7M38 24h7" />
        </svg>
      );
    case 'hospital':
      return (
        <svg {...baseSvgProps}>
          <path d="M10 41V11h28v30M16 17h5M27 17h5M16 25h5M27 25h5M20 41V32h8v9" />
        </svg>
      );
    case 'activity':
      return (
        <svg {...baseSvgProps}>
          <circle cx="17" cy="17" r="9" />
          <circle cx="31" cy="31" r="9" />
          <path d="M23 23l2 2" />
        </svg>
      );
    case 'brain':
      return (
        <svg {...baseSvgProps}>
          <path d="M22 8c-5-4-12 0-10 6-6 1-7 9-2 12-4 6 2 12 8 10 1 5 8 6 11 2V12c-1-3-4-5-7-4z" />
          <path d="M26 8c5-4 12 0 10 6 6 1 7 9 2 12 4 6-2 12-8 10-1 5-8 6-11 2V12c1-3 4-5 7-4z" />
        </svg>
      );
    case 'trending-up':
      return (
        <svg {...baseSvgProps}>
          <path d="M7 38h10V28h10V18h10V8" />
          <path d="m33 11 4-4 4 4" />
        </svg>
      );
    case 'tag':
      return (
        <svg {...baseSvgProps}>
          <path d="M7 20 21 7h17v17L24 38 7 21z" />
          <circle cx="31" cy="14" r="2" />
        </svg>
      );
    case 'toggle-switch':
      return (
        <svg {...baseSvgProps}>
          <rect height="20" rx="10" width="34" x="7" y="14" />
          <circle cx="18" cy="24" r="7" />
        </svg>
      );
    case 'laptop':
      return (
        <svg {...baseSvgProps}>
          <rect height="26" rx="3" width="36" x="6" y="8" />
          <path d="M18 41h12M24 34v7" />
          <path d="m16 20 5 5 10-11" />
        </svg>
      );
    case 'house':
      return (
        <svg {...baseSvgProps}>
          <path d="m6 22 18-15 18 15" />
          <path d="M10 20v21h28V20M20 41V29h8v12" />
        </svg>
      );
    default:
      return (
        <svg {...baseSvgProps}>
          <circle cx="24" cy="24" r="17" />
          <path d="M24 13v12l8 5" />
        </svg>
      );
  }
};

// FAQ Accordion Component with Framer Motion & Rich Visual Layout
const FAQItem = ({ question, shortAnswer, cards, summary, caution, links, isOpen, onToggle }) => {
  return (
    <div className="bg-white border border-[#E8DDCE] rounded-2xl overflow-hidden transition-all duration-300 shadow-sm hover:shadow-md h-full flex flex-col">
      <button
        onClick={onToggle}
        className="w-full px-6 py-5 min-h-[80px] md:min-h-[88px] text-left flex justify-between items-center hover:bg-[#FAF7F2] transition-colors group cursor-pointer"
        aria-expanded={isOpen}
      >
        <span className="font-bold text-[#123F13] md:text-lg pr-8">{question}</span>
        <div className={`shrink-0 text-[#123F13] transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>
          {isOpen ? <Minus size={20} /> : <Plus size={20} />}
        </div>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-6 text-[#3B352F] leading-relaxed border-t border-[#FAF7F2] pt-6 bg-gradient-to-b from-[#FCFAF7] to-[#FAF7F2]/40">
              <div className="space-y-6">
                {/* Short Answer Badge */}
                <div className="bg-gradient-to-r from-[#FAF7F2] to-[#F4EFE7] border border-[#E8DDCE] rounded-2xl p-6 text-center shadow-inner">
                  <small className="block text-[10px] font-bold text-[#8A8177] tracking-[0.16em] uppercase mb-2">Kort svar</small>
                  <strong className="block text-2xl md:text-3xl font-serif text-[#8C2F12] font-semibold leading-tight">{shortAnswer}</strong>
                </div>

                {/* 3-Card Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {cards.map((card, idx) => (
                    <article key={idx} className="bg-white/80 border border-[#E8DDCE] rounded-xl p-5 text-center flex flex-col items-center">
                      <div className="w-14 h-14 rounded-full bg-[#FAF7F2] border border-[#E8DDCE] flex items-center justify-center text-[#123F13] mb-4 shadow-sm">
                        <SVGIcon type={card.icon} className="w-7 h-7" />
                      </div>
                      <div className="w-6 h-[2px] bg-[#8C2F12] rounded-full mb-3" />
                      <h4 className="font-bold text-[#123F13] text-sm mb-2 leading-snug">{card.title}</h4>
                      <p className="text-xs text-[#3B352F]/70 leading-relaxed">{card.text}</p>
                    </article>
                  ))}
                </div>

                {/* Summary Box */}
                <div className="grid grid-cols-[56px_1fr] gap-4 items-center bg-[#FAF7F2]/90 border border-[#E8DDCE] rounded-xl p-5">
                  <div className="w-12 h-12 rounded-full bg-white border border-[#E8DDCE] flex items-center justify-center text-[#123F13] shadow-sm">
                    <SVGIcon type="checklist" className="w-6 h-6" />
                  </div>
                  <p className="text-sm text-[#3B352F]/90 leading-relaxed" dangerouslySetInnerHTML={{ __html: summary }} />
                </div>

                {/* Caution Box (Optional) */}
                {caution && (
                  <div className="border-l-4 border-[#8C2F12] bg-[#FFF8F4] rounded-r-xl p-4 text-xs text-[#3B352F]/80 leading-relaxed">
                    <strong className="text-[#123F13] font-bold block mb-1">Vigtigt:</strong>
                    {caution}
                  </div>
                )}

                {/* Links Section */}
                <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-[#E8DDCE]/40">
                  <span className="text-[10px] font-bold text-[#7A6E65] tracking-wider uppercase">Læs videre:</span>
                  {links.map((link, idx) => (
                    <Link
                      key={idx}
                      to={link.path}
                      className="inline-flex items-center gap-1.5 text-xs font-bold text-[#123F13] border border-[#8C2F12]/30 hover:border-[#8C2F12] bg-white px-3.5 py-2 rounded-full shadow-sm hover:bg-[#FFF8F4] transition-all hover:-translate-y-0.5"
                    >
                      {link.label} <span className="text-[#8C2F12] font-black">&rarr;</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Helbredsangst = () => {
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const canonicalUrl = "https://corehypnose.dk/helbredsangst";

  const faqData = [
    {
      question: "Er helbredsangst det samme som at bilde sig noget ind?",
      shortAnswer: "Nej",
      cards: [
        {
          icon: "eye",
          title: "Kropslige oplevelser er virkelige",
          text: "Du kan mærke tydelige og reelle fornemmelser i kroppen."
        },
        {
          icon: "chat",
          title: "Det handler om det, der sker bagefter",
          text: "Bekymring, analyse og søgen efter sikkerhed kan komme til at fylde meget."
        },
        {
          icon: "loop",
          title: "Mønstret kan gentage sig",
          text: "Tvivlen kan vende tilbage, selv efter at du kortvarigt har fået ro."
        }
      ],
      summary: "Helbredsangst handler ikke om, at du finder på symptomer. Fokus er på <strong>bekymringen, overvågningen og søgen efter sikkerhed omkring dem.</strong>",
      links: [
        { label: "Læs om angst", path: "/angst" },
        { label: "Metakognitiv terapi", path: "/metakognitiv-terapi" }
      ]
    },
    {
      question: "Hvordan ved jeg, om jeg bør gå til lægen?",
      shortAnswer: "Ved nye eller bekymrende symptomer: få en lægelig vurdering",
      cards: [
        {
          icon: "plus-medical",
          title: "Nye eller alvorlige symptomer",
          text: "De bør vurderes af en læge eller anden relevant sundhedsfaglig person."
        },
        {
          icon: "search",
          title: "Jeg vurderer ikke symptomer",
          text: "Jeg stiller ikke medicinske diagnoser og afgør ikke, om et symptom er farligt."
        },
        {
          icon: "shield-check",
          title: "Mit fokus er en anden del",
          text: "Jeg arbejder med bekymring, tjek, scanning og tvivl, når det bliver ved med at fylde."
        }
      ],
      summary: "Nye, alvorlige, vedvarende eller bekymrende symptomer bør vurderes sundhedsfagligt. <strong>Jeg arbejder ikke med medicinsk diagnostik.</strong>",
      caution: "Terapi erstatter ikke lægelig vurdering af nye eller bekymrende fysiske symptomer.",
      links: [
        { label: "Kontakt mig", path: "/kontakt" },
        { label: "Første session", path: "/bestil-tid" }
      ]
    },
    {
      question: "Hvad hvis lægen har overset noget?",
      shortAnswer: "Ingen kan give 100 % sikkerhed",
      cards: [
        {
          icon: "question-circle",
          title: "Tvivlen søger et endeligt svar",
          text: "Spørgsmålet kan vende tilbage, fordi fuldstændig sikkerhed ikke kan garanteres."
        },
        {
          icon: "clock",
          title: "Se på hvor meget tid tvivlen får",
          text: "Vi undersøger, hvor længe bekymringen får lov at køre."
        },
        {
          icon: "branch",
          title: "Se på hvad du føler dig nødt til at gøre",
          text: "Tjek, søgninger og nye svar kan blive en del af mønstret."
        }
      ],
      summary: "Jeg forsøger ikke at give dig en medicinsk garanti. Vi ser på, <strong>hvad der sker, når tvivlen opstår, og hvad du gør for at blive sikker igen.</strong>",
      links: [
        { label: "Metakognitiv terapi", path: "/metakognitiv-terapi" },
        { label: "Første session", path: "/bestil-tid" }
      ]
    },
    {
      question: "Hvorfor får jeg kun ro i kort tid, selv efter at jeg er blevet undersøgt?",
      shortAnswer: "Fordi roen kan blive knyttet til næste tjek",
      cards: [
        {
          icon: "arrow-right-circle",
          title: "Du får reel lettelse",
          text: "Et svar eller en undersøgelse kan give ro i øjeblikket."
        },
        {
          icon: "checklist",
          title: "En ny tvivl dukker op",
          text: "Så kan behovet for endnu et tjek eller endnu et svar begynde."
        },
        {
          icon: "loop",
          title: "Mønstret starter igen",
          text: "Kort lettelse kan efterfølges af ny usikkerhed og ny søgen efter sikkerhed."
        }
      ],
      summary: "Det er ikke mærkeligt, at du søger ro. Problemet kan opstå, når roen hele tiden skal <strong>skaffes igen gennem nye tjek, søgninger eller svar.</strong>",
      links: [
        { label: "Tankemylder", path: "/tankemylder" },
        { label: "Metakognitiv terapi", path: "/metakognitiv-terapi" }
      ]
    },
    {
      question: "Skal jeg bare stoppe med at tjekke og google?",
      shortAnswer: "Nej – ikke som en simpel ordre",
      cards: [
        {
          icon: "search-plus",
          title: "Det giver mening, at du søger",
          text: "Du prøver at få ro og forstå, hvad der sker."
        },
        {
          icon: "sliders",
          title: "Vi undersøger virkningen",
          text: "Hvad hjælper tjekket på kort sigt – og hvad sker der bagefter?"
        },
        {
          icon: "branch",
          title: "Målet er mere valgfrihed",
          text: "Du kan gradvist øve dig i ikke automatisk at reagere på enhver impuls til at tjekke."
        }
      ],
      summary: "Målet er ikke bare at forbyde tjek eller søgninger. Målet er <strong>større valgfrihed i forhold til, hvad du gør, når tvivlen opstår.</strong>",
      links: [
        { label: "Metakognitiv terapi", path: "/metakognitiv-terapi" },
        { label: "Book første session", path: "/bestil-tid" }
      ]
    },
    {
      question: "Hvorfor mærker jeg så meget i kroppen, når jeg først bliver bekymret?",
      shortAnswer: "Opmærksomhed kan gøre fornemmelsen mere fremtrædende",
      cards: [
        {
          icon: "user-focus",
          title: "Du retter fokus mod kroppen",
          text: "Et område eller en fornemmelse får mere opmærksomhed."
        },
        {
          icon: "target",
          title: "Du registrerer mere",
          text: "Når du holder øje, opdager du flere detaljer og små ændringer."
        },
        {
          icon: "loop",
          title: "Behovet for at overvåge vokser",
          text: "Det kan gøre det sværere at slippe fornemmelsen igen."
        }
      ],
      summary: "Det betyder ikke, at fornemmelsen er indbildt. Det betyder, at <strong>vedvarende opmærksomhed kan gøre den mere fremtrædende i bevidstheden.</strong>",
      caution: "Opmærksomhed kan påvirke, hvor meget en fornemmelse fylder. Det afgør ikke, om fornemmelsen er farlig eller ufarlig.",
      links: [
        { label: "Læs om angst", path: "/angst" },
        { label: "Metakognitiv terapi", path: "/metakognitiv-terapi" }
      ]
    },
    {
      question: "Kan jeg have en reel fysisk sygdom og samtidig have helbredsangst?",
      shortAnswer: "Ja",
      cards: [
        {
          icon: "hospital",
          title: "En fysisk sygdom kan være reel",
          text: "Helbredsangst udelukker ikke fysisk sygdom."
        },
        {
          icon: "activity",
          title: "Begge dele kan være til stede",
          text: "Du kan have en diagnose og samtidig bruge meget tid på bekymring og overvågning."
        },
        {
          icon: "shield-check",
          title: "Behandlingen må ikke benægte sygdom",
          text: "Mit fokus er den mentale belastning omkring tvivl, tjek og kontrol."
        }
      ],
      summary: "En fysisk sygdom og helbredsangst kan godt eksistere samtidig. <strong>Behandlingen handler ikke om at benægte eller bagatellisere fysisk sygdom.</strong>",
      links: [
        { label: "Kontakt mig", path: "/kontakt" },
        { label: "Metakognitiv terapi", path: "/metakognitiv-terapi" }
      ]
    },
    {
      question: "Hvordan arbejder du med helbredsangst?",
      shortAnswer: "Jeg arbejder med det, der sker, når tvivlen opstår",
      cards: [
        {
          icon: "brain",
          title: "Mindre tid i bekymring",
          text: "Vi ser på, hvor længe tankerne får lov at køre."
        },
        {
          icon: "target",
          title: "Mindre vedvarende scanning",
          text: "Vi undersøger, hvor meget opmærksomhed kroppen får."
        },
        {
          icon: "trending-up",
          title: "Mere frihed i handling",
          text: "Du træner i ikke automatisk at handle på enhver bekymrende tanke."
        }
      ],
      summary: "Jeg arbejder metakognitivt med <strong>bekymring, kropsscanning, analyse, tjek og søgen efter sikkerhed</strong> – ikke med at bevise, at hver enkelt tanke er forkert.",
      links: [
        { label: "Læs om metakognitiv terapi", path: "/metakognitiv-terapi" },
        { label: "Om Lars", path: "/om-lars" }
      ]
    },
    {
      question: "Kan jeg begynde med én session?",
      shortAnswer: "Ja",
      cards: [
        {
          icon: "calendar",
          title: "Start med én session",
          text: "Du behøver ikke beslutte et helt forløb på forhånd."
        },
        {
          icon: "tag",
          title: "375 kr.",
          text: "Første behandlingssession gælder kun nye klienter."
        },
        {
          icon: "clock",
          title: "50 minutter",
          text: "Vi skaber overblik og finder et realistisk næste skridt."
        }
      ],
      summary: "Du kan starte med <strong>én første behandlingssession til 375 kr.</strong> Der er ingen binding til et længere forløb.",
      links: [
        { label: "Book første session", path: "/bestil-tid" },
        { label: "Se priser", path: "/priser" }
      ]
    },
    {
      question: "Bruger du hypnose?",
      shortAnswer: "Kun hvis det giver mening for dig",
      cards: [
        {
          icon: "toggle-switch",
          title: "Det er valgfrit",
          text: "Du kan få hjælp uden hypnose."
        },
        {
          icon: "toggle-switch", // Re-use toggle switch for rolig guidning
          title: "Rolig guidning",
          text: "Hypnose bruges som fokuseret opmærksomhed og rolig guidning."
        },
        {
          icon: "shield-check",
          title: "Ikke en medicinsk undersøgelse",
          text: "Hypnose kan ikke afgøre, om et symptom er farligt."
        }
      ],
      summary: "Hypnose er <strong>en mulighed – ikke et krav.</strong> Den bruges ikke til at undersøge kroppen eller stille en diagnose.",
      links: [
        { label: "Læs om hypnose", path: "/hypnose" },
        { label: "Metakognitiv terapi", path: "/metakognitiv-terapi" }
      ]
    },
    {
      question: "Kan sessionen foregå online?",
      shortAnswer: "Ja",
      cards: [
        {
          icon: "laptop",
          title: "Online hjemmefra",
          text: "Du deltager fra et roligt sted med stabil forbindelse."
        },
        {
          icon: "house",
          title: "Valby er også muligt",
          text: "Du kan møde fysisk i klinikken på Valby Tingsted 7."
        },
        {
          icon: "sliders",
          title: "Du vælger rammen",
          text: "Vi finder den løsning, der passer bedst praktisk."
        }
      ],
      summary: "Du kan vælge session <strong>i Valby eller online.</strong>",
      links: [
        { label: "Læs om online sessioner", path: "/online-sessioner" },
        { label: "Book første session", path: "/bestil-tid" }
      ]
    }
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.summary.replace(/<\/?[^>]+(>|$)/g, "") // Strip HTML tags for JSON-LD
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>Helbredsangst – når tvivlen om kroppen bliver ved | Core Hypnose</title>
        <meta name="description" content="Få hjælp til helbredsangst i Valby eller online. Arbejd med bekymring, kropsscanning, tjek og søgen efter sikkerhed i et roligt og konkret forløb." />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content="Helbredsangst – når tvivlen om kroppen bliver ved | Core Hypnose" />
        <meta property="og:description" content="Få hjælp til helbredsangst i Valby eller online. Arbejd med bekymring, kropsscanning, tjek og søgen efter sikkerhed i et roligt og konkret forløb." />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="pt-24 pb-16 md:py-24 bg-gradient-to-br from-[#FCFAF7] to-[#FAF7F2] border-b border-[#E8DDCE]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-stretch">
            {/* Left Column */}
            <div className="lg:col-span-7 flex flex-col justify-center space-y-6">
              <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest flex items-center gap-2">
                <span className="w-8 h-[1px] bg-[#8C2F12]"></span>
                Hjælp til helbredsangst
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-[#123F13] leading-tight font-semibold">
                Helbredsangst – når tvivlen om kroppen bliver ved med at vende tilbage
              </h1>
              <p className="text-lg md:text-xl text-[#3B352F]/70 leading-relaxed max-w-2xl">
                Du kan være blevet undersøgt og stadig være i tvivl. Jeg hjælper dig med at arbejde med den bekymring, kropsscanning og søgen efter sikkerhed, der kan få uroen til at vende tilbage.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Link 
                  to="/bestil-tid" 
                  className="inline-flex items-center justify-center bg-[#8C2F12] text-white px-6 py-4 rounded-lg font-bold text-base hover:bg-[#a33a1a] transition-all shadow-md hover:-translate-y-0.5"
                >
                  Book første behandlingssession
                </Link>
                <a 
                  href="#moensteret" 
                  className="inline-flex items-center justify-center border-2 border-[#123F13] text-[#123F13] px-6 py-4 rounded-lg font-bold text-base hover:bg-[#123F13]/5 transition-all"
                >
                  Se hvordan mønsteret kan opstå &darr;
                </a>
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#3B352F]/60 font-bold pt-2">
                <span className="flex items-center gap-1.5"><Check size={16} className="text-[#123F13]" /> Valby Tingsted 7</span>
                <span className="flex items-center gap-1.5"><Check size={16} className="text-[#123F13]" /> Online session</span>
                <span className="flex items-center gap-1.5"><Check size={16} className="text-[#123F13]" /> Ingen binding til et forløb</span>
              </div>
            </div>

            {/* Right Column: Hero Card */}
            <div className="lg:col-span-5 flex items-center">
              <div className="bg-white border border-[#E8DDCE] rounded-2xl p-8 shadow-sm w-full relative overflow-hidden">
                <div className="absolute -right-6 -top-6 w-20 h-24 rounded-full bg-[#123F13]/5 pointer-events-none" />
                <div className="relative z-10 space-y-6">
                  <div>
                    <span className="inline-block bg-[#8C2F12]/10 text-[#8C2F12] px-2.5 py-1 rounded-md font-bold text-xs tracking-wider mb-3">
                      KUN FOR NYE KLIENTER
                    </span>
                    <h2 className="text-2xl font-serif text-[#123F13] font-bold">Første behandlingssession</h2>
                  </div>
                  
                  <div className="flex items-baseline gap-2 border-b border-[#FAF7F2] pb-4">
                    <span className="text-3xl font-bold text-[#8C2F12]">375 kr.</span>
                    <span className="text-sm text-[#3B352F]/60">/ 50 minutter</span>
                  </div>

                  <ul className="space-y-3 text-sm text-[#3B352F]/80">
                    <li className="flex items-start gap-2.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#8C2F12] shrink-0 mt-2" />
                      <span>Personlig kortlægning af det, der sætter tvivlen i gang</span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#8C2F12] shrink-0 mt-2" />
                      <span>Konkrete spørgsmål og praktiske øvelser</span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#8C2F12] shrink-0 mt-2" />
                      <span>Tydelig afklaring af et realistisk næste skridt</span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#8C2F12] shrink-0 mt-2" />
                      <span>Session i Valby eller online</span>
                    </li>
                  </ul>

                  <Link 
                    to="/bestil-tid" 
                    className="w-full inline-flex items-center justify-center bg-[#123F13] text-white px-5 py-3 rounded-lg font-bold text-sm hover:bg-[#1f5420] transition-all shadow-sm"
                  >
                    Book sessionen nu
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Recognition Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Column: Quote Card */}
            <div className="lg:col-span-5">
              <div className="bg-[#123F13] text-white p-8 rounded-2xl relative overflow-hidden flex flex-col justify-center min-h-[280px]">
                <div className="text-white/10 absolute -top-4 -left-2 text-8xl font-serif select-none pointer-events-none">“</div>
                <p className="text-xl md:text-2xl font-serif text-white italic leading-relaxed relative z-10 mb-6">
                  “Jeg ved godt, at jeg er blevet undersøgt. Men hvad nu hvis noget alligevel er blevet overset?”
                </p>
                <span className="text-xs font-bold text-white/70 uppercase tracking-wider">
                  En tanke mange med helbredsangst kan genkende
                </span>
              </div>
            </div>

            {/* Right Column */}
            <div className="lg:col-span-7 space-y-8">
              <div className="space-y-3">
                <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest">Måske kender du det</span>
                <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] font-semibold">
                  Du prøver at få ro – men tvivlen kommer tilbage
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Step 1 */}
                <div className="flex gap-4 items-start">
                  <div className="w-8 h-8 rounded-full bg-[#8C2F12]/10 text-[#8C2F12] flex items-center justify-center font-bold text-sm shrink-0">1</div>
                  <p className="text-[#3B352F]/80 text-sm leading-relaxed">
                    Du mærker noget i kroppen og bliver straks mere opmærksom.
                  </p>
                </div>

                {/* Step 2 */}
                <div className="flex gap-4 items-start">
                  <div className="w-8 h-8 rounded-full bg-[#8C2F12]/10 text-[#8C2F12] flex items-center justify-center font-bold text-sm shrink-0">2</div>
                  <p className="text-[#3B352F]/80 text-sm leading-relaxed">
                    Måske undersøger du området, googler symptomet eller spørger andre, hvad de tænker.
                  </p>
                </div>

                {/* Step 3 */}
                <div className="flex gap-4 items-start">
                  <div className="w-8 h-8 rounded-full bg-[#8C2F12]/10 text-[#8C2F12] flex items-center justify-center font-bold text-sm shrink-0">3</div>
                  <p className="text-[#3B352F]/80 text-sm leading-relaxed">
                    Du kan få ro i et øjeblik. Så kommer en ny tanke: “Hvad nu hvis?”
                  </p>
                </div>

                {/* Step 4 */}
                <div className="flex gap-4 items-start">
                  <div className="w-8 h-8 rounded-full bg-[#8C2F12]/10 text-[#8C2F12] flex items-center justify-center font-bold text-sm shrink-0">4</div>
                  <p className="text-[#3B352F]/80 text-sm leading-relaxed">
                    Du mærker efter igen og forsøger endnu en gang at blive helt sikker.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pattern Section */}
      <section id="moensteret" className="py-20 bg-[#FAF7F2] border-y border-[#E8DDCE]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-left max-w-3xl mb-16">
            <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest">Når mønsteret gentager sig</span>
            <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mt-3 mb-6 font-semibold">
              Sådan kan helbredsangst blive ved med at fylde
            </h2>
            <p className="text-lg text-[#3B352F]/70">
              Det er ikke altid den første kropslige fornemmelse, der tager mest plads. For mange er det det, der sker bagefter.
            </p>
          </div>

          {/* Flow Diagram */}
          <div className="bg-white border border-[#E8DDCE] rounded-2xl p-8 shadow-sm relative overflow-hidden max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 relative">
              {/* Step 1 */}
              <div className="bg-[#FAF7F2] p-5 rounded-xl border border-[#E8DDCE]/60 flex flex-col items-center text-center relative">
                <span className="w-7 h-7 rounded-full bg-[#8C2F12] text-white flex items-center justify-center font-bold text-xs mb-3">1</span>
                <strong className="text-[#123F13] text-sm leading-snug">Du mærker noget</strong>
              </div>

              {/* Step 2 */}
              <div className="bg-[#FAF7F2] p-5 rounded-xl border border-[#E8DDCE]/60 flex flex-col items-center text-center relative">
                <span className="w-7 h-7 rounded-full bg-[#8C2F12] text-white flex items-center justify-center font-bold text-xs mb-3">2</span>
                <strong className="text-[#123F13] text-sm leading-snug">Tvivlen melder sig</strong>
              </div>

              {/* Step 3 */}
              <div className="bg-[#FAF7F2] p-5 rounded-xl border border-[#E8DDCE]/60 flex flex-col items-center text-center relative">
                <span className="w-7 h-7 rounded-full bg-[#8C2F12] text-white flex items-center justify-center font-bold text-xs mb-3">3</span>
                <strong className="text-[#123F13] text-sm leading-snug">Mere opmærksomhed mod kroppen</strong>
              </div>

              {/* Step 4 */}
              <div className="bg-[#FAF7F2] p-5 rounded-xl border border-[#E8DDCE]/60 flex flex-col items-center text-center relative">
                <span className="w-7 h-7 rounded-full bg-[#8C2F12] text-white flex items-center justify-center font-bold text-xs mb-3">4</span>
                <strong className="text-[#123F13] text-sm leading-snug">Du tjekker, søger eller analyserer</strong>
              </div>

              {/* Step 5 */}
              <div className="bg-[#FAF7F2] p-5 rounded-xl border border-[#E8DDCE]/60 flex flex-col items-center text-center relative">
                <span className="w-7 h-7 rounded-full bg-[#8C2F12] text-white flex items-center justify-center font-bold text-xs mb-3">5</span>
                <strong className="text-[#123F13] text-sm leading-snug">Du får måske ro i kort tid</strong>
              </div>

              {/* Step 6 */}
              <div className="bg-[#FAF7F2] p-5 rounded-xl border border-[#E8DDCE]/60 flex flex-col items-center text-center relative">
                <span className="w-7 h-7 rounded-full bg-[#8C2F12] text-white flex items-center justify-center font-bold text-xs mb-3">6</span>
                <strong className="text-[#123F13] text-sm leading-snug">Tvivlen vender tilbage</strong>
              </div>
            </div>

            {/* Info Note */}
            <div className="mt-8 bg-[#FAF7F2] border border-[#E8DDCE] rounded-xl p-5 flex gap-4 items-start">
              <div className="w-6 h-6 rounded-full bg-[#123F13] text-white flex items-center justify-center font-serif font-bold italic shrink-0 text-sm">i</div>
              <p className="text-sm text-[#3B352F]/80 leading-relaxed">
                Det betyder ikke, at du selv har valgt mønsteret. Det viser, hvorfor det kan være så svært at få varig ro, selv når du gør alt, hvad du kan for at blive sikker.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Medical Section */}
      <section className="py-20 bg-[#123F13] text-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Column */}
            <div className="lg:col-span-5 space-y-4">
              <span className="text-[#E8DDCE] font-bold text-xs uppercase tracking-widest">Dine oplevelser bliver taget alvorligt</span>
              <h2 className="text-3xl md:text-4xl font-serif text-[#FAF7F2] font-semibold leading-tight">
                Dine kropslige oplevelser er ikke “bare noget, du bilder dig ind”
              </h2>
            </div>

            {/* Right Column */}
            <div className="lg:col-span-7 space-y-6">
              <p className="text-lg text-[#FAF7F2]/80 leading-relaxed">
                Kropslige fornemmelser er virkelige oplevelser. Nye, alvorlige, vedvarende eller bekymrende symptomer bør vurderes af en læge eller anden relevant sundhedsfaglig person.
              </p>
              
              <div className="bg-white/[0.05] border border-[#FAF7F2]/20 text-white p-8 rounded-2xl shadow-md space-y-4">
                <p className="font-bold text-lg text-[#FAF7F2]">
                  Jeg vurderer ikke, om et fysisk symptom er farligt.
                </p>
                <p className="text-[#FAF7F2]/80 text-sm leading-relaxed">
                  Jeg arbejder med den del, hvor bekymring, overvågning, tjek og tvivl bliver ved med at fylde.
                </p>
                <p className="text-[#FAF7F2]/80 text-sm leading-relaxed">
                  Du kan godt have en reel fysisk sygdom og samtidig opleve helbredsangst. Behandlingen handler ikke om at benægte eller bagatellisere sygdom.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Relief Section */}
      <section className="py-20 bg-[#FAF7F2] border-y border-[#E8DDCE]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-left max-w-3xl mb-16">
            <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest">Når sikkerhed skal skaffes igen</span>
            <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mt-3 mb-6 font-semibold">
              Hvorfor roen nogle gange kun holder kort
            </h2>
            <p className="text-lg text-[#3B352F]/70">
              Det er ikke forkert at ønske tryghed. Problemet kan opstå, når trygheden hele tiden skal skaffes igen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-12">
            {/* Card 1 */}
            <div className="bg-white p-8 rounded-2xl border border-[#E8DDCE] flex flex-col items-start text-left shadow-sm">
              <div className="w-12 h-12 rounded-full bg-[#123F13]/10 text-[#123F13] flex items-center justify-center font-bold text-lg mb-4">✓</div>
              <h3 className="text-xl font-bold text-[#123F13] mb-3">Når du tjekker kroppen</h3>
              <p className="text-[#3B352F]/70 text-sm leading-relaxed">
                Et tjek kan give reel lettelse. Men hvis roen kun holder, indtil den næste tvivl dukker op, kan behovet for endnu et tjek vokse.
              </p>
            </div>

            {/* Card 2 */}
            <div className="bg-white p-8 rounded-2xl border border-[#E8DDCE] flex flex-col items-start text-left shadow-sm">
              <div className="w-12 h-12 rounded-full bg-[#123F13]/10 text-[#123F13] flex items-center justify-center font-bold text-lg mb-4">?</div>
              <h3 className="text-xl font-bold text-[#123F13] mb-3">Når du søger svar på nettet</h3>
              <p className="text-[#3B352F]/70 text-sm leading-relaxed">
                En søgning kan begynde som et forsøg på at få ro. Mange mulige forklaringer kan samtidig give nye spørgsmål og mere usikkerhed.
              </p>
            </div>

            {/* Card 3 */}
            <div className="bg-white p-8 rounded-2xl border border-[#E8DDCE] flex flex-col items-start text-left shadow-sm">
              <div className="w-12 h-12 rounded-full bg-[#123F13]/10 text-[#123F13] flex items-center justify-center font-bold text-lg mb-4">...</div>
              <h3 className="text-xl font-bold text-[#123F13] mb-3">Når du spørger andre</h3>
              <p className="text-[#3B352F]/70 text-sm leading-relaxed">
                Et svar kan berolige dig. Men hvis tvivlen hurtigt vender tilbage, kan du få brug for endnu et svar.
              </p>
            </div>
          </div>

          {/* Relief Line */}
          <div className="bg-white border border-[#E8DDCE] rounded-2xl p-6 max-w-6xl mx-auto shadow-sm flex flex-col sm:flex-row justify-center items-center gap-4 sm:gap-6 text-center">
            <div className="font-bold text-[#123F13] flex-1 text-center">Tvivl</div>
            <div className="h-[1px] w-full sm:w-[1px] sm:h-6 bg-[#E8DDCE] shrink-0"></div>
            <div className="font-bold text-[#123F13] flex-1 text-center">Søgen efter sikkerhed</div>
            <div className="h-[1px] w-full sm:w-[1px] sm:h-6 bg-[#E8DDCE] shrink-0"></div>
            <div className="font-bold text-[#123F13] flex-1 text-center">Kort lettelse</div>
            <div className="h-[1px] w-full sm:w-[1px] sm:h-6 bg-[#E8DDCE] shrink-0"></div>
            <div className="font-bold text-[#123F13] flex-1 text-center">Ny tvivl</div>
          </div>
        </div>
      </section>

      {/* Attention Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Column */}
            <div className="lg:col-span-7 space-y-6">
              <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest">Når opmærksomheden bliver fanget</span>
              <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] font-semibold leading-tight">
                Jo mere du holder øje, desto mere kan fornemmelsen fylde
              </h2>
              <p className="text-[#3B352F]/70 text-base leading-relaxed">
                Når du bliver bekymret for et bestemt område i kroppen, kan det være svært ikke at holde øje med det.
              </p>
              <p className="text-[#3B352F]/70 text-base leading-relaxed">
                Jo mere opmærksomhed du retter mod en fornemmelse, desto mere fremtrædende kan den opleves.
              </p>
              <div className="bg-[#FAF7F2] border-l-4 border-[#123F13] p-4 text-sm text-[#3B352F]/80 rounded-r-xl">
                Det betyder ikke, at fornemmelsen er indbildt. Det betyder heller ikke, at jeg kan vurdere, om den er farlig.
              </div>
            </div>

            {/* Right Column: Loop Card */}
            <div className="lg:col-span-5">
              <div className="bg-[#FAF7F2] border border-[#E8DDCE] rounded-2xl p-8 shadow-sm space-y-5">
                <h3 className="font-bold text-[#123F13] text-lg border-b border-[#E8DDCE]/40 pb-3">Opmærksomhedens spiral</h3>
                
                <div className="space-y-4">
                  <div className="flex gap-3 items-start">
                    <span className="w-6 h-6 rounded-full bg-[#8C2F12]/10 text-[#8C2F12] flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">1</span>
                    <p className="text-sm text-[#3B352F]/80">Du bliver bekymret for en fornemmelse</p>
                  </div>
                  <div className="flex gap-3 items-start">
                    <span className="w-6 h-6 rounded-full bg-[#8C2F12]/10 text-[#8C2F12] flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">2</span>
                    <p className="text-sm text-[#3B352F]/80">Du retter mere opmærksomhed mod området</p>
                  </div>
                  <div className="flex gap-3 items-start">
                    <span className="w-6 h-6 rounded-full bg-[#8C2F12]/10 text-[#8C2F12] flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">3</span>
                    <p className="text-sm text-[#3B352F]/80">Fornemmelsen fylder mere i din bevidsthed</p>
                  </div>
                  <div className="flex gap-3 items-start">
                    <span className="w-6 h-6 rounded-full bg-[#8C2F12]/10 text-[#8C2F12] flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">4</span>
                    <p className="text-sm text-[#3B352F]/80">Du får endnu mere lyst til at overvåge</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Freedom Section */}
      <section className="py-20 bg-[#FAF7F2] border-y border-[#E8DDCE]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-left max-w-3xl mb-16">
            <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest">Et andet forhold til tvivlen</span>
            <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mt-3 mb-6 font-semibold">
              Du behøver ikke blive 100 % sikker for at få mere frihed
            </h2>
            <p className="text-lg text-[#3B352F]/70">
              Jeg arbejder ikke på at overbevise dig om, at der aldrig kan ske noget med din krop. Det ville ingen kunne garantere.
            </p>
          </div>

          <div className="bg-[#123F13] border border-[#1d521e] rounded-2xl p-8 md:p-12 shadow-lg max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-11 gap-6 items-center">
              {/* Left Side */}
              <div className="md:col-span-5 space-y-3">
                <span className="text-xs font-bold tracking-wider text-[#FAF7F2]/70 uppercase">PÅ AUTOPILOT</span>
                <h3 className="text-xl font-bold text-white">Når reaktionen går på autopilot</h3>
                <p className="text-sm text-[#FAF7F2]/80 leading-relaxed">
                  En bekymrende tanke opstår, og du føler dig straks nødt til at tjekke, analysere eller søge svar.
                </p>
              </div>

              {/* Arrow */}
              <div className="md:col-span-1 flex justify-center text-white/40 font-black text-2xl py-4 md:py-0">
                &rarr;
              </div>

              {/* Right Side */}
              <div className="md:col-span-5 space-y-3">
                <span className="text-xs font-bold tracking-wider text-[#FAF7F2]/70 uppercase">VALGFRIHED</span>
                <h3 className="text-xl font-bold text-white">Når du får mere valgfrihed</h3>
                <p className="text-sm text-[#FAF7F2]/80 leading-relaxed">
                  Du opdager tanken, giver den mindre tid og vælger mere bevidst, hvad du vil rette din opmærksomhed mod.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Method Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Column */}
            <div className="lg:col-span-6 space-y-6">
              <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest">Sådan arbejder jeg med helbredsangst</span>
              <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] font-semibold leading-tight">
                Vi ser på, hvad der sker, når tvivlen opstår
              </h2>
              <p className="text-base text-[#3B352F]/70 leading-relaxed">
                Målet er ikke, at du aldrig må få en bekymrende tanke. Målet er, at tanken ikke automatisk skal bestemme, hvor meget du tjekker, analyserer eller overvåger.
              </p>
              <p className="text-base text-[#3B352F]/70 leading-relaxed">
                Jeg arbejder med metakognitive principper, hvor fokus er mindre på at diskutere, om hver enkelt tanke er sand, og mere på det, du gør med tanken, når den dukker op.
              </p>
            </div>

            {/* Right Column: Questions List */}
            <div className="lg:col-span-6">
              <div className="bg-[#FAF7F2] border border-[#E8DDCE] rounded-2xl p-8 shadow-sm space-y-4">
                <h3 className="font-bold text-[#123F13] text-lg border-b border-[#E8DDCE]/40 pb-3">Spørgsmål vi undersøger sammen:</h3>
                
                <div className="space-y-3.5">
                  <div className="flex gap-3 items-start">
                    <span className="w-6 h-6 rounded-full bg-[#123F13]/10 text-[#123F13] flex items-center justify-center font-bold text-sm shrink-0">?</span>
                    <p className="text-sm text-[#3B352F]/80">Hvor meget tid får bekymringen lov til at køre?</p>
                  </div>
                  <div className="flex gap-3 items-start">
                    <span className="w-6 h-6 rounded-full bg-[#123F13]/10 text-[#123F13] flex items-center justify-center font-bold text-sm shrink-0">?</span>
                    <p className="text-sm text-[#3B352F]/80">Hvor meget holder du øje med kroppen?</p>
                  </div>
                  <div className="flex gap-3 items-start">
                    <span className="w-6 h-6 rounded-full bg-[#123F13]/10 text-[#123F13] flex items-center justify-center font-bold text-sm shrink-0">?</span>
                    <p className="text-sm text-[#3B352F]/80">Hvad gør du for at blive sikker?</p>
                  </div>
                  <div className="flex gap-3 items-start">
                    <span className="w-6 h-6 rounded-full bg-[#123F13]/10 text-[#123F13] flex items-center justify-center font-bold text-sm shrink-0">?</span>
                    <p className="text-sm text-[#3B352F]/80">Hvor længe holder lettelsen?</p>
                  </div>
                  <div className="flex gap-3 items-start">
                    <span className="w-6 h-6 rounded-full bg-[#123F13]/10 text-[#123F13] flex items-center justify-center font-bold text-sm shrink-0">?</span>
                    <p className="text-sm text-[#3B352F]/80">Hvad sker der, når tvivlen vender tilbage?</p>
                  </div>
                  <div className="flex gap-3 items-start border-t border-[#E8DDCE]/40 pt-3 mt-3">
                    <span className="w-6 h-6 rounded-full bg-[#8C2F12]/10 text-[#8C2F12] flex items-center justify-center font-bold text-sm shrink-0">&rarr;</span>
                    <p className="text-sm font-bold text-[#123F13]">Hvordan kan du få mere frihed til at lade tvivlen være der uden straks at handle på den?</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hypnosis Section */}
      <section className="py-20 bg-[#FAF7F2] border-y border-[#E8DDCE]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="bg-white border border-[#E8DDCE] rounded-3xl p-8 md:p-12 shadow-sm max-w-6xl mx-auto flex flex-col md:flex-row gap-8 items-center">
            <div className="w-16 h-14 rounded-full bg-[#123F13]/10 text-[#123F13] flex items-center justify-center font-serif text-3xl shrink-0">○</div>
            <div className="space-y-4">
              <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest">Valgfri støtte</span>
              <h2 className="text-2xl md:text-3xl font-serif text-[#123F13] font-semibold leading-tight">
                Hypnose kan bruges som støtte – hvis det giver mening for dig
              </h2>
              <p className="text-sm text-[#3B352F]/70 leading-relaxed">
                Hypnose er ikke en måde at undersøge kroppen på og kan ikke fortælle, om et symptom er farligt.
              </p>
              <p className="text-sm text-[#3B352F]/70 leading-relaxed">
                Jeg kan bruge hypnose som rolig guidning og fokuseret opmærksomhed, hvis det passer til dig og det, vi arbejder med. Det kan støtte arbejdet med mindre kamp, mere rolig opmærksomhed og det at lade en fornemmelse være til stede uden straks at analysere den.
              </p>
              <p className="text-xs font-bold text-[#8C2F12] uppercase tracking-wider">
                Du kan altid vælge hypnose fra.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Session Section */}
      <section className="py-20 bg-[#123F13] text-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Column */}
            <div className="lg:col-span-5 space-y-6">
              <span className="text-[#FAF7F2]/90 font-bold text-xs uppercase tracking-widest">Et konkret første skridt</span>
              <h2 className="text-3xl md:text-4xl font-serif text-white font-semibold leading-tight">
                Din første behandlingssession
              </h2>
              <p className="text-base text-[#FAF7F2]/80 leading-relaxed">
                Den første session handler om at skabe overblik over, hvad der sætter tvivlen i gang, hvad du gør for at få ro, og hvad der kan være et realistisk næste skridt.
              </p>
              <p className="text-base text-[#FAF7F2]/80 leading-relaxed">
                Du behøver ikke vide præcis, hvilken metode du har brug for, før du kommer.
              </p>
            </div>

            {/* Right Column: Session Card */}
            <div className="lg:col-span-7">
              <div className="bg-[#FAF7F2] border border-[#E8DDCE] rounded-2xl p-8 shadow-sm space-y-6">
                <h3 className="font-serif text-[#123F13] text-xl font-bold border-b border-[#E8DDCE]/40 pb-4">
                  Første behandlingssession for nye klienter
                </h3>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white border border-[#E8DDCE]/60 rounded-xl p-4 text-center">
                    <strong className="block text-xl font-bold text-[#8C2F12]">375 kr.</strong>
                    <span className="text-xs text-[#3B352F]/60">Pris</span>
                  </div>
                  <div className="bg-white border border-[#E8DDCE] rounded-xl p-5 text-center">
                    <strong className="block text-xl font-bold text-[#123F13]">50 min.</strong>
                    <span className="text-sm text-[#3B352F]/60">Varighed</span>
                  </div>
                  <div className="bg-white border border-[#E8DDCE] rounded-xl p-5 text-center flex flex-col justify-center">
                    <strong className="text-sm font-bold text-[#123F13]">Valby</strong>
                    <span className="text-xs text-[#3B352F]/60">I klinikken</span>
                  </div>
                  <div className="bg-white border border-[#E8DDCE] rounded-xl p-5 text-center flex flex-col justify-center">
                    <strong className="text-sm font-bold text-[#123F13]">Online</strong>
                    <span className="text-xs text-[#3B352F]/60">Hjemmefra</span>
                  </div>
                </div>

                <ul className="space-y-2.5 text-sm text-[#3B352F]/80">
                  <li className="flex items-start gap-2">
                    <span className="text-[#8C2F12] font-bold shrink-0 mt-0.5">✓</span>
                    <span>Personlig kortlægning af det, der sætter bekymringen i gang</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#8C2F12] font-bold shrink-0 mt-0.5">✓</span>
                    <span>Konkrete metakognitive spørgsmål</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#8C2F12] font-bold shrink-0 mt-0.5">✓</span>
                    <span>Praktiske øvelser og arbejde med opmærksomhed</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#8C2F12] font-bold shrink-0 mt-0.5">✓</span>
                    <span>Tydelig afklaring af næste skridt</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#8C2F12] font-bold shrink-0 mt-0.5">✓</span>
                    <span>En personlig plan for, hvad du kan arbejde videre med</span>
                  </li>
                </ul>

                <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t border-[#E8DDCE]/40">
                  <Link 
                    to="/bestil-tid" 
                    className="inline-flex items-center justify-center bg-[#8C2F12] text-white px-6 py-3 rounded-lg font-bold text-sm hover:bg-[#a33a1a] transition-all shadow-sm shrink-0"
                  >
                    Book sessionen nu
                  </Link>
                  <a 
                    href="tel:+4527204671" 
                    className="inline-flex items-center justify-center border-2 border-[#123F13] text-[#123F13] px-6 py-3 rounded-lg font-bold text-sm hover:bg-[#123F13]/5 transition-all"
                  >
                    Ring 27 20 46 71
                  </a>
                </div>

                <div className="pt-4 border-t border-[#E8DDCE]/40 space-y-4 text-xs text-[#3B352F]/70 leading-relaxed">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                    <div>
                      <strong className="block text-[#123F13] font-semibold mb-1">Priser & rabatter:</strong>
                      <ul className="space-y-1 list-disc list-inside">
                        <li>Første behandlingssession: 375 kr. / 50 minutter</li>
                        <li>Almindelig session: 1.800 kr.</li>
                        <li>Studerende og pensionister: 1.350 kr.</li>
                        <li>Core-rabat ved lægehenvisning: 300 kr.</li>
                      </ul>
                    </div>
                    <div>
                      <strong className="block text-[#123F13] font-semibold mb-1">Afbud & vilkår:</strong>
                      <ul className="space-y-1 list-disc list-inside">
                        <li>Afbud eller ændring senest 24 timer før</li>
                        <li>24-timersreglen gælder også ved akut sygdom</li>
                        <li>Hvis man ikke kan møde fysisk, kan den planlagte session gennemføres online i stedet</li>
                        <li>Betaling sker altid inden sessionen starter</li>
                      </ul>
                    </div>
                  </div>
                  <p className="pt-2 text-center border-t border-[#E8DDCE]/20">
                    <Link to="/priser" className="font-bold text-[#8C2F12] hover:underline inline-flex items-center gap-1">
                      Se alle gældende priser og vilkår her <ArrowRight size={12} />
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-[#FAF7F2] border-y border-[#E8DDCE]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest">Ofte stillede spørgsmål</span>
            <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mt-3 mb-6 font-semibold">
              Spørgsmål mange har, før de tager kontakt
            </h2>
            <p className="text-lg text-[#3B352F]/70">
              Åbn et spørgsmål og få svaret forklaret visuelt, roligt og konkret.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-6xl mx-auto">
            {faqData.map((item, idx) => (
              <FAQItem
                key={idx}
                question={item.question}
                shortAnswer={item.shortAnswer}
                cards={item.cards}
                summary={item.summary}
                caution={item.caution}
                links={item.links}
                isOpen={openFaqIndex === idx}
                onToggle={() => toggleFaq(idx)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Related Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-3xl mb-16">
            <span className="text-[#8C2F12] font-bold text-xs uppercase tracking-widest">Måske er dette også relevant for dig</span>
            <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mt-3 mb-6 font-semibold">
              Læs videre i det, der passer til din situation
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl">
            <Link to="/metakognitiv-terapi" className="bg-[#FAF7F2] border border-[#E8DDCE] rounded-2xl p-6 hover:shadow-md transition-all group flex flex-col justify-between h-40">
              <h3 className="font-serif text-lg font-bold text-[#123F13] group-hover:text-[#8C2F12] transition-colors leading-snug">Metakognitiv terapi</h3>
              <span className="text-xs font-bold text-[#8C2F12] flex items-center gap-1">Læs mere <ArrowRight size={14} /></span>
            </Link>
            <Link to="/panikangst" className="bg-[#FAF7F2] border border-[#E8DDCE] rounded-2xl p-6 hover:shadow-md transition-all group flex flex-col justify-between h-40">
              <h3 className="font-serif text-lg font-bold text-[#123F13] group-hover:text-[#8C2F12] transition-colors leading-snug">Panikangst og frygt for kropslige signaler</h3>
              <span className="text-xs font-bold text-[#8C2F12] flex items-center gap-1">Læs mere <ArrowRight size={14} /></span>
            </Link>
            <Link to="/ocd" className="bg-[#FAF7F2] border border-[#E8DDCE] rounded-2xl p-6 hover:shadow-md transition-all group flex flex-col justify-between h-40">
              <h3 className="font-serif text-lg font-bold text-[#123F13] group-hover:text-[#8C2F12] transition-colors leading-snug">OCD, tvivl og gentagne tjek</h3>
              <span className="text-xs font-bold text-[#8C2F12] flex items-center gap-1">Læs mere <ArrowRight size={14} /></span>
            </Link>
            <Link to="/online-sessioner" className="bg-[#FAF7F2] border border-[#E8DDCE] rounded-2xl p-6 hover:shadow-md transition-all group flex flex-col justify-between h-40">
              <h3 className="font-serif text-lg font-bold text-[#123F13] group-hover:text-[#8C2F12] transition-colors leading-snug">Online sessioner</h3>
              <span className="text-xs font-bold text-[#8C2F12] flex items-center gap-1">Læs mere <ArrowRight size={14} /></span>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#123F13] to-[#1f5420] text-white relative overflow-hidden">
        <div className="absolute -right-32 -bottom-32 w-96 h-96 rounded-full bg-white/[0.03] pointer-events-none" />
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10 space-y-6 flex flex-col items-center">
          <div className="mb-2">
            <img 
              src="https://assets.auxodev.dk/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/corehypnose-lak-segl.png" 
              alt="Core Kvalitetsstempel" 
              className="w-24 h-24 rotate-[-6deg] drop-shadow-[0_10px_20px_rgba(0,0,0,0.3)] object-contain mx-auto"
            />
          </div>
          <span className="text-[#FAF7F2]/80 font-bold text-xs uppercase tracking-widest block">Et roligere næste skridt</span>
          <h2 className="text-3xl md:text-5xl font-serif text-[#FAF7F2] font-semibold leading-tight">
            Du behøver ikke have regnet det hele ud, før du tager kontakt
          </h2>
          <p className="text-lg text-[#FAF7F2]/80 max-w-2xl mx-auto leading-relaxed">
            Vi kan begynde med at skabe overblik over, hvad der sætter tvivlen i gang, og finde et realistisk næste skridt.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4 w-full">
            <Link 
              to="/bestil-tid" 
              className="inline-flex items-center justify-center bg-[#8C2F12] text-white px-8 py-4 rounded-lg font-bold text-base hover:bg-[#a33a1a] transition-all shadow-md hover:-translate-y-0.5"
            >
              Book første behandlingssession
            </Link>
            <Link 
              to="/kontakt" 
              className="inline-flex items-center justify-center border-2 border-[#FAF7F2] text-[#FAF7F2] px-8 py-4 rounded-lg font-bold text-base hover:bg-white/10 transition-all"
            >
              Kontakt mig
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Helbredsangst;